import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { projectSpacesApi, commentNotificationSettingsApi } from '../../services/fileShareApi';
import { formatAppDate } from '../../services/appConfigRuntime';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add,
  Folder,
  Edit,
  Delete,
  Refresh,
  Settings,
  FolderOpen,
  Archive,
  Unarchive,
  MoreVert,
  ViewModule,
  ViewList,
  Link as LinkIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import ProjectStorageConfig from './ProjectStorageConfig';
import ProjectConnectors from './ProjectConnectors';
import { isConnectorConnected } from './fileShareConnectors';
import { storageApi } from '../../services/fileShareApi';
import fileShareApi from '../../services/fileShareApi';
import axios from 'axios';

const LAYOUT_STORAGE_KEY = 'fileshare.projectSpaces.layout';
const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const parseEmailList = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  const seen = new Set();
  const out = [];
  raw.split(/[,;\s]+/).forEach((part) => {
    const email = part.trim().toLowerCase();
    if (!email || seen.has(email)) return;
    seen.add(email);
    out.push(email);
  });
  return out;
};

const isValidEmail = (email) => EMAIL_PATTERN.test((email || '').trim());

// Date formatting helper — App Config DATE_FORMAT
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return formatAppDate(date);
  } catch (error) {
    return 'N/A';
  }
};

/** Overflow ⋮ menu for project actions (grid + list) */
const ProjectActionsMenu = ({
  project,
  isArchivedView,
  restoring,
  onConnectors,
  onStorage,
  onRename,
  onArchive,
  onRestore,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const close = () => setAnchorEl(null);

  const run = (fn) => (e) => {
    e.stopPropagation();
    close();
    fn?.(project);
  };

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          aria-label="More project actions"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={(e) => {
          e?.stopPropagation?.();
          close();
        }}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={run(onRename)}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Rename project</ListItemText>
        </MenuItem>
        <MenuItem onClick={run(onConnectors)}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText>Configure connectors</ListItemText>
        </MenuItem>
        <MenuItem onClick={run(onStorage)}>
          <ListItemIcon><FolderOpen fontSize="small" /></ListItemIcon>
          <ListItemText>Storage / root path</ListItemText>
        </MenuItem>
        <Divider />
        {!isArchivedView ? (
          <MenuItem onClick={run(onArchive)}>
            <ListItemIcon><Archive fontSize="small" /></ListItemIcon>
            <ListItemText>Archive</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={run(onRestore)} disabled={restoring}>
            <ListItemIcon><Unarchive fontSize="small" /></ListItemIcon>
            <ListItemText>Restore</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={run(onDelete)} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const ProjectSpaces = ({ onProjectSelect, selectedProject, refreshTrigger, onRefresh }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectNameError, setNewProjectNameError] = useState('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [projectToRename, setProjectToRename] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
  const [notificationEmailList, setNotificationEmailList] = useState([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailDraftError, setEmailDraftError] = useState('');
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [emailSettingsSaving, setEmailSettingsSaving] = useState(false);
  const [showStorageConfigDialog, setShowStorageConfigDialog] = useState(false);
  const [selectedProjectForStorage, setSelectedProjectForStorage] = useState(null);
  const [showConnectorConfigDialog, setShowConnectorConfigDialog] = useState(false);
  const [selectedProjectForConnector, setSelectedProjectForConnector] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Persist active vs archived list in the URL so Back from a project returns to the same section
  const viewMode = searchParams.get('view') === 'archived' ? 'archived' : 'active';
  const setViewMode = (mode) => {
    const next = mode === 'archived' ? 'archived' : 'active';
    try {
      sessionStorage.setItem('fileshare.projects.viewMode', next);
    } catch {
      /* ignore */
    }
    if (next === 'archived') {
      setSearchParams({ view: 'archived' }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };
  const [layoutMode, setLayoutMode] = useState(() => {
    try {
      const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
      return saved === 'list' || saved === 'grid' ? saved : 'grid';
    } catch {
      return 'grid';
    }
  });
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [projectToArchive, setProjectToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  
  // Connector configuration state for create dialog
  const [newProjectStorageConnector, setNewProjectStorageConnector] = useState('');
  const [newProjectForgeConnector, setNewProjectForgeConnector] = useState('');
  const [availableConnectors, setAvailableConnectors] = useState({
    storage: [],
    forge: []
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchConnectors();
  }, [refreshTrigger, viewMode]);

  // Keep return-target in sync when list mode comes from the URL
  useEffect(() => {
    try {
      sessionStorage.setItem('fileshare.projects.viewMode', viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const fetchConnectors = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenantId');
      
      // Fetch connector tokens for this tenant
      const connectorTokensResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/'}synctrack/sync-connectors/tenant/${tenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const connectorTokens = connectorTokensResponse.data || [];
      
      if (connectorTokens.length === 0) {
        setAvailableConnectors({ storage: [], forge: [] });
        return;
      }
      
      // Fetch master connectors to get connector type information
      const masterConnectorsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/'}synctrack/master-connector`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const masterConnectors = masterConnectorsResponse.data || [];
      
      // Join connector tokens with master connectors to get type and name
      const connectorsWithType = connectorTokens.map(token => {
        const masterConnector = masterConnectors.find(mc => 
          mc.mcId?.toString() === token.mcId?.toString() ||
          mc.mcRecId?.toString() === token.mcId?.toString()
        );
        
        return {
          id: token.id, // This is the connector token ID (ctd_id)
          mcId: token.mcId, // Master connector ID for reference
          connectorName: (token.description || '').trim()
            || (masterConnector ? masterConnector.mcName : `Unknown (ID: ${token.mcId})`),
          mcName: masterConnector?.mcName || null,
          connectorType: masterConnector ? masterConnector.mcConnectorType : null,
          status: token.status,
          // Include all token fields
          ...token
        };
      });
      
      // Filter by connector type + Connected status only
      const storageConnectors = connectorsWithType.filter(connector => {
        const type = connector.connectorType;
        const isStorageType = type && (
          type === 'STORAGE' ||
          type === 'MINIO_STORAGE' ||
          type === 'AWS_S3_STORAGE' ||
          type === 'EGNYTE_STORAGE' ||
          type === 'GOOGLE_DRIVE_STORAGE' ||
          type === 'SHAREPOINT_STORAGE' ||
          type === 'DROPBOX_STORAGE'
        );
        return isStorageType && isConnectorConnected(connector.status);
      });
      
      // Translation connectors (currently Autodesk/Forge, but designed to support future translation types)
      const translationConnectors = connectorsWithType.filter(connector => {
        const type = connector.connectorType;
        // Support Autodesk/Forge connector types (can be expanded for other translation engines)
        const isTranslationType = type && (
          type === 'AUTODESK' || 
          type === 'FORGE' || 
          type === 'AUTODESK_FORGE' ||
          type.toLowerCase() === 'autodesk' ||
          type.toLowerCase() === 'forge'
        );
        return isTranslationType && isConnectorConnected(connector.status);
      });
      
      setAvailableConnectors({
        storage: storageConnectors,
        forge: translationConnectors // Using 'forge' key for backward compatibility, but contains all translation connectors
      });
      
      console.log('Available connected connector tokens:', { 
        storage: storageConnectors, 
        forge: translationConnectors,
        translationConnectors: translationConnectors // Added for clarity
      });
    } catch (err) {
      console.error('Error fetching connector tokens:', err);
      setAvailableConnectors({ storage: [], forge: [] });
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // TODO: Replace with actual API call
      // const response = await fetch('/api/projects', {
      //   headers: {
      //     'X-Tenant-Id': localStorage.getItem('tenantId'),
      //     'X-User-Id': localStorage.getItem('userId'),
      //   },
      // });
      // const data = await response.json();
      
      // Fetch projects from API
      const response = await projectSpacesApi.getAll({
        archived: viewMode === 'archived',
      });
      const list = [...(response.data || [])].sort((a, b) => {
        const aTime = new Date(a.createdAt || 0).getTime();
        const bTime = new Date(b.createdAt || 0).getTime();
        return bTime - aTime; // newest first
      });
      setProjects(list);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      const detail = status ? ` (${status})` : '';
      setError(`Failed to load projects${detail}: ${msg || 'Unknown error'}`);
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    const trimmedName = newProjectName.trim();
    if (!trimmedName) {
      setNewProjectNameError('Project Name is required');
      return;
    }
    setNewProjectNameError('');

    setCreating(true);
    try {
      const projectData = {
        projectName: trimmedName,
        description: '',
      };

      const newProject = await projectSpacesApi.create(projectData);

      // Get project ID from response
      const projectId = newProject.data?.projectId || newProject.data?.id || newProject.projectId;
      
      if (!projectId) {
        console.error('No project ID in response:', newProject);
      } else {
        // Assign storage connector if provided
        if (newProjectStorageConnector) {
          try {
            await fileShareApi.put(`/projects/${projectId}/storage-connector`, {
              storageConnectorId: newProjectStorageConnector
            });
            console.log('Storage connector assigned to new project');
          } catch (storageErr) {
            console.error('Error assigning storage connector:', storageErr);
            // Don't fail the entire project creation if connector assignment fails
          }
        }

        // Assign Forge connector if provided
        if (newProjectForgeConnector) {
          try {
            await fileShareApi.put(`/projects/${projectId}/forge-connector`, {
              forgeConnectorId: newProjectForgeConnector
            });
            console.log('Forge connector assigned to new project');
          } catch (forgeErr) {
            console.error('Error assigning Forge connector:', forgeErr);
            // Don't fail the entire project creation if connector assignment fails
          }
        }
      }

      // Refresh the project list
      await fetchProjects();

      // Reset form fields
      setNewProjectName('');
      setNewProjectNameError('');
      setNewProjectStorageConnector('');
      setNewProjectForgeConnector('');
      setCreating(false);
      setShowCreateDialog(false);
    } catch (err) {
      console.error('Error creating project:', err);
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      toast.error(msg ? `Could not create project: ${msg}` : 'Could not create project');
    } finally {
      setCreating(false);
    }
  };

  const openRenameDialog = (project) => {
    setProjectToRename(project);
    setRenameValue(project?.projectName || '');
    setRenameError('');
    setRenameDialogOpen(true);
  };

  const openEmailSettings = async () => {
    setEmailSettingsOpen(true);
    setEmailSettingsLoading(true);
    setEmailDraft('');
    setEmailDraftError('');
    try {
      const res = await commentNotificationSettingsApi.get();
      const emails = res.data?.data?.notificationEmails || res.data?.notificationEmails || '';
      setNotificationEmailList(parseEmailList(emails));
    } catch (err) {
      console.error('Failed to load notification emails:', err);
      toast.error('Could not load notification emails');
      setNotificationEmailList([]);
    } finally {
      setEmailSettingsLoading(false);
    }
  };

  const closeEmailSettings = () => {
    if (emailSettingsSaving) return;
    setEmailSettingsOpen(false);
    setEmailDraft('');
    setEmailDraftError('');
  };

  /** Persist immediately so users don't need a separate Save click. */
  const persistEmailList = async (list, { successToast } = {}) => {
    try {
      setEmailSettingsSaving(true);
      setEmailDraftError('');
      await commentNotificationSettingsApi.save(list.join(', '));
      if (successToast) {
        toast.success(successToast);
      }
      return true;
    } catch (err) {
      console.error('Failed to save notification emails:', err);
      const msg = err.response?.data?.message || err.message;
      toast.error(msg ? `Could not save: ${msg}` : 'Could not save notification emails');
      return false;
    } finally {
      setEmailSettingsSaving(false);
    }
  };

  const addNotificationEmail = async () => {
    const raw = emailDraft.trim();
    if (!raw) {
      setEmailDraftError('Enter an email address');
      return false;
    }
    // Single address or paste of several (comma / space separated)
    let toAdd = parseEmailList(raw);
    if (!toAdd.length) {
      toAdd = [raw.toLowerCase()];
    }
    const invalid = toAdd.filter((e) => !isValidEmail(e));
    if (invalid.length) {
      setEmailDraftError(
        invalid.length === 1
          ? `"${invalid[0]}" is not a valid email`
          : `Invalid email(s): ${invalid.join(', ')}`
      );
      return false;
    }
    const seen = new Set(notificationEmailList);
    const next = [...notificationEmailList];
    toAdd.forEach((email) => {
      if (!seen.has(email)) {
        seen.add(email);
        next.push(email);
      }
    });
    setNotificationEmailList(next);
    setEmailDraft('');
    setEmailDraftError('');
    const ok = await persistEmailList(next, { successToast: 'Email saved' });
    if (!ok) {
      // Reload authoritative list if save failed
      try {
        const res = await commentNotificationSettingsApi.get();
        const emails = res.data?.data?.notificationEmails || res.data?.notificationEmails || '';
        setNotificationEmailList(parseEmailList(emails));
      } catch {
        /* keep optimistic list */
      }
    }
    return ok;
  };

  const removeNotificationEmail = async (email) => {
    const next = notificationEmailList.filter((e) => e !== email);
    setNotificationEmailList(next);
    const ok = await persistEmailList(next, { successToast: 'Email removed' });
    if (!ok) {
      try {
        const res = await commentNotificationSettingsApi.get();
        const emails = res.data?.data?.notificationEmails || res.data?.notificationEmails || '';
        setNotificationEmailList(parseEmailList(emails));
      } catch {
        /* keep optimistic list */
      }
    }
  };

  const closeRenameDialog = () => {
    if (renaming) return;
    setRenameDialogOpen(false);
    setProjectToRename(null);
    setRenameValue('');
    setRenameError('');
  };

  const handleRenameConfirm = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenameError('Project Name is required');
      return;
    }
    if (!projectToRename?.projectId) return;
    if (trimmed === projectToRename.projectName) {
      closeRenameDialog();
      return;
    }

    try {
      setRenaming(true);
      setRenameError('');
      await projectSpacesApi.update(projectToRename.projectId, { projectName: trimmed });
      toast.success(`Project renamed to "${trimmed}"`);
      await fetchProjects();

      if (selectedProject?.projectId === projectToRename.projectId) {
        onProjectSelect({ ...selectedProject, projectName: trimmed });
      }
      setRenameDialogOpen(false);
      setProjectToRename(null);
      setRenameValue('');
    } catch (err) {
      console.error('Error renaming project:', err);
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      setRenameError(msg || 'Could not rename project');
    } finally {
      setRenaming(false);
    }
  };

  const openDeleteConfirm = (project) => {
    setProjectToDelete(project);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setProjectToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete?.projectId) return;

    const projectId = projectToDelete.projectId;
    const projectName = projectToDelete.projectName || 'project';

    try {
      setDeleting(true);
      await projectSpacesApi.delete(projectId);
      toast.success(`Project "${projectName}" deleted successfully`);
      await fetchProjects();

      if (selectedProject?.projectId === projectId) {
        onProjectSelect(null);
      }
      setDeleteConfirmOpen(false);
      setProjectToDelete(null);
    } catch (err) {
      console.error('Error deleting project:', err);
      toast.error(
        `Failed to delete project: ${err.response?.data?.message || err.message}`
      );
    } finally {
      setDeleting(false);
    }
  };

  const openArchiveConfirm = (project) => {
    setProjectToArchive(project);
    setArchiveConfirmOpen(true);
  };

  const handleArchiveCancel = () => {
    setArchiveConfirmOpen(false);
    setProjectToArchive(null);
  };

  const handleArchiveConfirm = async () => {
    if (!projectToArchive?.projectId) return;

    const projectId = projectToArchive.projectId;
    const projectName = projectToArchive.projectName || 'project';

    try {
      setArchiving(true);
      await projectSpacesApi.archive(projectId);
      toast.success(`Project "${projectName}" moved to archive`);
      await fetchProjects();

      if (selectedProject?.projectId === projectId) {
        onProjectSelect(null);
      }
      setArchiveConfirmOpen(false);
      setProjectToArchive(null);
    } catch (err) {
      console.error('Error archiving project:', err);
      toast.error(
        `Failed to archive project: ${err.response?.data?.message || err.message}`
      );
    } finally {
      setArchiving(false);
    }
  };

  const handleRestoreProject = async (project) => {
    if (!project?.projectId) return;

    const projectId = project.projectId;
    const projectName = project.projectName || 'project';

    try {
      setRestoring(true);
      await projectSpacesApi.unarchive(projectId);
      toast.success(`Project "${projectName}" restored to active projects`);
      await fetchProjects();
    } catch (err) {
      console.error('Error restoring project:', err);
      toast.error(
        `Failed to restore project: ${err.response?.data?.message || err.message}`
      );
    } finally {
      setRestoring(false);
    }
  };

  const isArchivedView = viewMode === 'archived';

  const handleLayoutChange = (_event, next) => {
    if (!next) return;
    setLayoutMode(next);
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const projectActionProps = (project) => ({
    project,
    isArchivedView,
    restoring,
    onConnectors: (p) => {
      setSelectedProjectForConnector(p);
      setShowConnectorConfigDialog(true);
    },
    onStorage: (p) => {
      setSelectedProjectForStorage(p);
      setShowStorageConfigDialog(true);
    },
    onRename: openRenameDialog,
    onArchive: openArchiveConfirm,
    onRestore: handleRestoreProject,
    onDelete: openDeleteConfirm,
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexShrink: 0,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h6">
            {isArchivedView ? 'Archived Projects' : 'Project Spaces'} ({projects.length})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isArchivedView
              ? 'Completed or inactive projects kept for reference. Restore to show on the main list again.'
              : 'Active projects only. Archive old projects to keep this page clear.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <ToggleButtonGroup
            value={layoutMode}
            exclusive
            size="small"
            onChange={handleLayoutChange}
            aria-label="Project layout"
            sx={{
              bgcolor: 'background.paper',
              '& .MuiToggleButton-root': {
                px: 1.25,
                textTransform: 'none',
                gap: 0.5,
              },
            }}
          >
            <ToggleButton value="grid" aria-label="Grid view">
              <ViewModule fontSize="small" />
              Grid
            </ToggleButton>
            <ToggleButton value="list" aria-label="List view">
              <ViewList fontSize="small" />
              List
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            size="small"
            startIcon={isArchivedView ? <Folder /> : <Archive />}
            onClick={() => setViewMode(isArchivedView ? 'active' : 'archived')}
          >
            {isArchivedView ? 'Back to active' : 'View archived'}
          </Button>
          <Tooltip title="Emails">
            <IconButton onClick={openEmailSettings} size="small" aria-label="Emails">
              <EmailIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onRefresh} size="small">
            <Refresh />
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LinkIcon />}
            onClick={() => navigate('/file-share/share-links')}
            sx={{ textTransform: 'none' }}
          >
            Share Links
          </Button>
          {!isArchivedView && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowCreateDialog(true)}
            >
              New Project
            </Button>
          )}
        </Box>
      </Box>

      {projects.length === 0 ? (
        <Alert severity="info">
          {isArchivedView
            ? 'No archived projects yet.'
            : 'No active projects yet. Create a new project to get started.'}
        </Alert>
      ) : (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.5,
          pb: 3,
          // Keep last row fully visible above the scroll edge
          scrollPaddingBottom: 24,
        }}
      >
        {layoutMode === 'list' ? (
          <Box
            sx={{
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            {/* Column header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: isArchivedView
                  ? 'minmax(0, 1.6fr) minmax(140px, 0.9fr) minmax(110px, 0.7fr) minmax(110px, 0.7fr) 140px 48px'
                  : 'minmax(0, 1.8fr) minmax(160px, 1fr) minmax(120px, 0.7fr) 140px 48px',
                gap: 2,
                alignItems: 'center',
                px: 2.5,
                py: 1.25,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {[
                'Project',
                'Created by',
                'Created',
                ...(isArchivedView ? ['Archived'] : []),
                'Contents',
                '',
              ].map((label, i) => (
                <Typography
                  key={`${label}-${i}`}
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontSize: '0.68rem',
                    textAlign: i >= (isArchivedView ? 4 : 3) ? 'right' : 'left',
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {projects.map((project, index) => {
                const selected = selectedProject?.projectId === project.projectId;
                const creator = project.createdByName || project.createdBy || 'Unknown User';
                return (
                  <Box
                    component="li"
                    key={project.projectId}
                    onClick={() => onProjectSelect(project)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: isArchivedView
                        ? 'minmax(0, 1.6fr) minmax(140px, 0.9fr) minmax(110px, 0.7fr) minmax(110px, 0.7fr) 140px 48px'
                        : 'minmax(0, 1.8fr) minmax(160px, 1fr) minmax(120px, 0.7fr) 140px 48px',
                      gap: 2,
                      alignItems: 'center',
                      px: 2.5,
                      py: 1.5,
                      cursor: 'pointer',
                      borderBottom:
                        index < projects.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: selected ? 'rgba(128, 0, 32, 0.06)' : 'transparent',
                      transition: 'background-color 0.15s ease',
                      boxShadow: selected
                        ? (theme) => `inset 3px 0 0 0 ${theme.palette.primary.main}`
                        : 'none',
                      '&:hover': {
                        bgcolor: selected
                          ? 'rgba(128, 0, 32, 0.08)'
                          : 'rgba(0, 0, 0, 0.025)',
                        '& .list-row-actions': { opacity: 1 },
                      },
                    }}
                  >
                    {/* Project */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.25,
                          bgcolor: selected ? 'primary.main' : 'rgba(128, 0, 32, 0.08)',
                          color: selected ? 'primary.contrastText' : 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Folder sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          noWrap
                          title={project.projectName}
                          sx={{ lineHeight: 1.35 }}
                        >
                          {project.projectName}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Created by */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      title={creator}
                      sx={{ fontSize: '0.8125rem' }}
                    >
                      {creator}
                    </Typography>

                    {/* Created on */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatDate(project.createdAt)}
                    </Typography>

                    {isArchivedView && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        title={
                          project.archivedByName
                            ? `${formatDate(project.archivedAt)} · ${project.archivedByName}`
                            : formatDate(project.archivedAt)
                        }
                        sx={{ fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {formatDate(project.archivedAt)}
                      </Typography>
                    )}

                    {/* Contents */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 0.75,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Chip
                        size="small"
                        label={`${project.folderCount ?? 0} folders`}
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: 'grey.100',
                          border: 'none',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                      <Chip
                        size="small"
                        label={`${project.fileCount ?? 0} files`}
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: 'grey.100',
                          border: 'none',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    </Box>

                    {/* Actions */}
                    <Box
                      className="list-row-actions"
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        opacity: { xs: 1, md: 0.55 },
                        transition: 'opacity 0.15s',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ProjectActionsMenu {...projectActionProps(project)} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Grid container spacing={2.5} sx={{ pt: 0.5, pb: 0.5 }}>
            {projects.map((project) => {
              const selected = selectedProject?.projectId === project.projectId;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={project.projectId}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      borderRadius: 2.5,
                      // Fixed border width — hover uses shadow + lift, not a thicker border
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      boxShadow: selected
                        ? '0 4px 14px rgba(0,0,0,0.08)'
                        : '0 1px 2px rgba(0,0,0,0.04)',
                      backgroundColor: 'background.paper',
                      transition:
                        'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                      willChange: 'transform',
                      '&:hover': {
                        borderColor: selected ? 'primary.main' : 'divider',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        transform: 'translateY(-3px)',
                      },
                    }}
                    onClick={() => onProjectSelect(project)}
                  >
                    <CardContent sx={{ flex: 1, pt: 2.5, px: 2.5, pb: 2.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              opacity: 0.92,
                            }}
                          >
                            <Folder sx={{ fontSize: 22 }} />
                          </Box>
                          <Typography
                            variant="subtitle1"
                            component="div"
                            fontWeight={700}
                            noWrap
                            title={project.projectName}
                            sx={{ lineHeight: 1.3 }}
                          >
                            {project.projectName}
                          </Typography>
                        </Box>
                        <Box onClick={(e) => e.stopPropagation()} sx={{ mt: -0.5, mr: -1 }}>
                          <ProjectActionsMenu {...projectActionProps(project)} />
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{ mb: 0.5 }}
                      >
                        {project.createdByName || project.createdBy || 'Unknown User'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        Created {formatDate(project.createdAt)}
                      </Typography>

                      {isArchivedView && project.archivedAt && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                          Archived {formatDate(project.archivedAt)}
                          {project.archivedByName ? ` · ${project.archivedByName}` : ''}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${project.folderCount ?? 0} folders`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 24, fontSize: '0.75rem' }}
                        />
                        <Chip
                          label={`${project.fileCount ?? 0} files`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 24, fontSize: '0.75rem' }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
      )}

      {/* Create Project Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setNewProjectNameError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            required
            fullWidth
            variant="outlined"
            value={newProjectName}
            onChange={(e) => {
              setNewProjectName(e.target.value);
              if (newProjectNameError) setNewProjectNameError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !creating) {
                e.preventDefault();
                handleCreateProject();
              }
            }}
            error={Boolean(newProjectNameError)}
            helperText={newProjectNameError}
            sx={{ mt: 2 }}
          />

          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Connector Configuration (Optional)
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Storage Connector</InputLabel>
            <Select
              value={newProjectStorageConnector}
              onChange={(e) => setNewProjectStorageConnector(e.target.value)}
              label="Storage Connector"
            >
              <MenuItem value="">
                <em>Use Default Storage</em>
              </MenuItem>
              {availableConnectors.storage.map((connector) => (
                <MenuItem key={connector.id} value={connector.id}>
                  {connector.connectorName}
                </MenuItem>
              ))}
              {availableConnectors.storage.length === 0 && (
                <MenuItem disabled>No Connected storage connectors</MenuItem>
              )}
            </Select>
            {availableConnectors.storage.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No Connected storage connectors. Connect one under Connector Token first.
              </Alert>
            )}
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Translation Connector (Forge/Autodesk)</InputLabel>
            <Select
              value={newProjectForgeConnector}
              onChange={(e) => setNewProjectForgeConnector(e.target.value)}
              label="Translation Connector (Forge/Autodesk)"
            >
              <MenuItem value="">
                <em>Use Default Translation Credentials</em>
              </MenuItem>
              {availableConnectors.forge.map((connector) => (
                <MenuItem key={connector.id} value={connector.id}>
                  {connector.connectorName}
                </MenuItem>
              ))}
              {availableConnectors.forge.length === 0 && (
                <MenuItem disabled>No Connected translation connectors</MenuItem>
              )}
            </Select>
            {availableConnectors.forge.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No Connected Autodesk/Forge connectors. Connect one under Connector Token first.
              </Alert>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowCreateDialog(false);
            setNewProjectName('');
            setNewProjectNameError('');
            setNewProjectStorageConnector('');
            setNewProjectForgeConnector('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={creating || !newProjectName.trim()}
          >
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Storage Configuration Dialog */}
      {selectedProjectForStorage && (
        <ProjectStorageConfig
          projectId={selectedProjectForStorage.projectId}
          projectName={selectedProjectForStorage.projectName}
          open={showStorageConfigDialog}
          onClose={() => {
            setShowStorageConfigDialog(false);
            setSelectedProjectForStorage(null);
          }}
          onSuccess={() => {
            // Refresh projects after storage configuration
            fetchProjects();
          }}
        />
      )}

      {/* Tenant-wide comment notification emails */}
      <Dialog
        open={emailSettingsOpen}
        onClose={closeEmailSettings}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Emails</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add one or more addresses. They receive a message whenever someone comments on
            any public FileShare viewer link. Changes save automatically.
          </Typography>
          {emailSettingsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 32 }}>
                {notificationEmailList.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No emails added yet.
                  </Typography>
                ) : (
                  notificationEmailList.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={emailSettingsSaving ? undefined : () => removeNotificationEmail(email)}
                      size="small"
                    />
                  ))
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  label="Add email"
                  placeholder="name@company.com"
                  value={emailDraft}
                  onChange={(e) => {
                    setEmailDraft(e.target.value);
                    if (emailDraftError) setEmailDraftError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addNotificationEmail();
                    }
                  }}
                  error={Boolean(emailDraftError)}
                  helperText={
                    emailDraftError ||
                    'Press Enter or Add — saved automatically. Invalid emails show in red.'
                  }
                  disabled={emailSettingsSaving}
                />
                <Button
                  variant="outlined"
                  onClick={addNotificationEmail}
                  disabled={emailSettingsSaving || !emailDraft.trim()}
                  sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                >
                  {emailSettingsSaving ? <CircularProgress size={18} color="inherit" /> : 'Add'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmailSettings} disabled={emailSettingsSaving} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Project Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={closeRenameDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="rename-project-dialog-title"
      >
        <DialogTitle id="rename-project-dialog-title">Rename Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            required
            fullWidth
            variant="outlined"
            value={renameValue}
            disabled={renaming}
            onChange={(e) => {
              setRenameValue(e.target.value);
              if (renameError) setRenameError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !renaming) {
                e.preventDefault();
                handleRenameConfirm();
              }
            }}
            error={Boolean(renameError)}
            helperText={renameError}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenameDialog} disabled={renaming}>
            Cancel
          </Button>
          <Button
            onClick={handleRenameConfirm}
            variant="contained"
            disabled={renaming || !renameValue.trim()}
          >
            {renaming ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connector Configuration Dialog */}
      {selectedProjectForConnector && (
        <Dialog
          open={showConnectorConfigDialog}
          onClose={() => {
            setShowConnectorConfigDialog(false);
            setSelectedProjectForConnector(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            Configure Connectors — {selectedProjectForConnector.projectName}
          </DialogTitle>
          <DialogContent sx={{ pt: 1, px: 3, pb: 2 }}>
            <ProjectConnectors
              variant="dialog"
              projectId={selectedProjectForConnector.projectId}
              projectName={selectedProjectForConnector.projectName}
              onConnectorUpdate={(connectorType, connectorId) => {
                console.log(`Connector updated: ${connectorType} = ${connectorId}`);
                fetchProjects();
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setShowConnectorConfigDialog(false);
                setSelectedProjectForConnector(null);
              }}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete confirmation — same pattern as Sync Flow / DynamicContainer */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-project-dialog-title"
      >
        <DialogTitle id="delete-project-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            <AlertTitle>Warning</AlertTitle>
            Are you sure you want to delete
            {projectToDelete?.projectName ? ` "${projectToDelete.projectName}"` : ' this project'}?
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            autoFocus
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={archiveConfirmOpen}
        onClose={handleArchiveCancel}
        aria-labelledby="archive-project-dialog-title"
      >
        <DialogTitle id="archive-project-dialog-title">Move to archive?</DialogTitle>
        <DialogContent>
          <Typography>
            Move
            {projectToArchive?.projectName ? ` "${projectToArchive.projectName}"` : ' this project'}
            {' '}to the archive? Files and history are kept. You can restore it later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveCancel} color="primary" disabled={archiving}>
            Cancel
          </Button>
          <Button
            onClick={handleArchiveConfirm}
            color="primary"
            variant="contained"
            disabled={archiving}
          >
            {archiving ? <CircularProgress size={20} color="inherit" /> : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectSpaces;

